'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/core/supabase';
import { toast } from 'sonner';
type UserContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  login: (isExtension?: boolean) => Promise<void>;
  loginWithEmail: (email: string, password: string, isSignUp?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 尝试从 storage 中恢复 session
    const getSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        console.error('获取 session 失败:', error);
      }
      if (session) {
        console.log('恢复 session 成功:', session);
        setUser(session.user);
        setSession(session);
      }
      setIsLoading(false);
    };

    getSession();

    // 监听 Supabase 认证状态变化（登录、登出、token刷新等）
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('auth 状态变化:', event, session);
        if (session) {
          setUser(session.user);
          setSession(session);
        } else {
          setUser(null);
          setSession(null);
        }
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // 登录方法：这里以 Google OAuth 为例
  const login = async (isExtension = false) => {
    try {
        // 只有在没有现有重定向URL时才保存当前URL，避免覆盖AuthGuard设置的值
        if (typeof window !== 'undefined') {
            const existingRedirectUrl = localStorage.getItem('auth_redirect_url');
            if (!existingRedirectUrl) {
                localStorage.setItem('auth_redirect_url', window.location.href);
            }
        }
        
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                // 使用简单的回调URL，添加extension参数标识
                redirectTo: `${window.location.origin}/auth/minimal-callback${isExtension ? '?extension=true' : ''}`,
                queryParams: {
                    access_type: 'offline',
                    prompt: 'consent',
                },
            }
        });
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        if (error) throw error;
    } catch (error: any) {
        console.error('Login error:', error);
        toast.error('Login failed, please try again');
    }
  };

  // 邮箱密码登录/注册
  const loginWithEmail = async (email: string, password: string, isSignUp = false) => {
    try {
      if (isSignUp) {
        // 注册新用户
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          }
        });
        
        if (error) {
          // 处理注册错误
          if (error.message?.includes("already registered")) {
            toast.error("此邮箱已注册，请直接登录或使用其他邮箱");
          } else if (error.message?.includes("password")) {
            toast.error("密码不符合要求，至少需要6个字符");
          } else {
            toast.error(`注册失败: ${error.message}`);
          }
          throw error; // 抛出错误但不再显示额外的消息
        }
        
        // 如果需要验证邮箱，提示用户
        // 注意：成功消息由调用方处理
        
      } else {
        // 登录已有用户
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) {
          // 显示错误提示
          if (error.message === "Email not confirmed") {
            toast.error("邮箱未验证，请先验证邮箱");
          } else if (error.message === "Invalid login credentials") {
            toast.error("邮箱或密码错误，请重新输入");
          } else {
            toast.error(`登录失败: ${error.message}`);
          }
          throw error; // 抛出错误但不再显示额外的消息
        }
        
        // 登录成功
        if (data && data.user) {
          setUser(data.user);
          if (data.session) setSession(data.session);
          // 注意：成功消息由调用方处理
        }
      }
      
      // 移除localStorage设置逻辑，因为这会覆盖AuthGuard保存的正确重定向路径
      // 重定向逻辑完全由登录页面处理
      
    } catch (error: any) {
      // 记录错误但不显示额外的toast消息，因为已经在上面显示了特定的错误
      console.error('Email authentication error:', error);
      throw error; // 重新抛出以便调用方知道操作失败
    }
  };

  // 登出方法
  const logout = async () => {
    try {
        const { error } = await supabase.auth.signOut();
        
        // 即使 signOut 失败（如会话已过期），也要清理本地状态
        setUser(null);
        setSession(null);
        
        // 如果错误是会话缺失，这实际上是正常的（用户已经登出）
        if (error && error.message !== 'Auth session missing!') {
            throw error;
        }
        
        toast.success('Successfully logged out');
    } catch (error: any) {
        console.error('Logout error:', error);
        // 仍然清理本地状态，因为用户点击了登出
        setUser(null);
        setSession(null);
        toast.error('Logout failed, but you have been signed out locally');
    }
  };

  // 忘记密码 - 发送重置密码邮件
  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        if (error.message?.includes('not found')) {
          toast.error('该邮箱未注册，请检查邮箱地址或先注册账户');
        } else {
          toast.error(`发送重置邮件失败: ${error.message}`);
        }
        throw error;
      }

      toast.success('重置密码邮件已发送，请检查您的邮箱');
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  // 更新密码
  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        if (error.message?.includes('password')) {
          toast.error('密码不符合要求，至少需要6个字符');
        } else {
          toast.error(`更新密码失败: ${error.message}`);
        }
        throw error;
      }

      toast.success('密码更新成功');
    } catch (error: any) {
      console.error('Update password error:', error);
      throw error;
    }
  };

  return (
    <UserContext.Provider value={{ user, session, isLoading, login, loginWithEmail, logout, setUser, resetPassword, updatePassword }}>
      {children}
    </UserContext.Provider>
  );
};

// 自定义 Hook，方便组件中调用用户上下文
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser 必须在 UserProvider 内部使用');
  }
  return context;
};
